import { selectTotalVisibleQuestionsWithAiScoring } from "features/qualityForm/qualityFormSlice";
import {
    selectDoesDateLastChangedExist,
    selectHasAiScoringFailed,
    selectIsAiScoringPending,
    selectTotalVisibleQuestionsWithAiAnswer,
} from "features/scorable/scorableSlice";
import { useStrings } from "features/i18n/useStrings";
import { shallowEqual } from "react-redux";
import {
    GuxIcon,
    GuxRadialLoading,
    GuxTooltip,
} from "genesys-spark-components-react";
import strings from "./AiScoringStatus.strings";
import "./AiScoringStatus.less";
import { useAppSelector } from "app/hooks";
import { selectScorable } from "../scorableSlice";
import { useResolvedLocale } from "features/i18n/useLocale";
import { useTimeZone } from "features/timezone/useTimeZone";
import { useMemo } from "react";
import { EvaluationStatusEnum } from "@genesys/cloud-frontend/dist/mjs/models";
import PreviewTag from "features/qualityForm/formEditor/sidePanel/PreviewTag";

export function AiScoringStatus() {
    const t = useStrings(strings);
    const locale = useResolvedLocale();
    const { totalQuestions, totalQuestionsWithAiAnswer } = useAppSelector(
        (state) => {
            const totalQuestions =
                selectTotalVisibleQuestionsWithAiScoring(state).length;

            const totalQuestionsWithAiAnswer =
                selectTotalVisibleQuestionsWithAiAnswer(state);
            return {
                totalQuestions,
                totalQuestionsWithAiAnswer,
            };
        },
        shallowEqual
    );

    const isAiScoringPending = useAppSelector((state) => {
        return selectIsAiScoringPending(state);
    });

    const aiScoringDateExists = useAppSelector((state) => {
        return selectDoesDateLastChangedExist(state);
    });

    const hasAiScoringFailed = useAppSelector((state) => {
        return selectHasAiScoringFailed(state);
    });

    const isPendingOrInProgress = useAppSelector((state) => {
        return (
            (state.scorable.data.scorable?.status ===
                EvaluationStatusEnum.INPROGRESS ||
                state.scorable.data.scorable?.status ===
                    EvaluationStatusEnum.PENDING) ??
            false
        );
    });

    const showProcessingStatus = isAiScoringPending && isPendingOrInProgress;

    const timeZone = useTimeZone();
    const lastProcessedDate = useAppSelector((state) => {
        const scorable = selectScorable(state);
        return scorable?.aiScoring?.dateLastChanged;
    });
    const options: Intl.DateTimeFormatOptions = useMemo(() => {
        return {
            day: "numeric",
            month: "long",
            year: "numeric",
            hour: "numeric",
            minute: "numeric",
            timeZone: timeZone,
            timeZoneName: "short",
        };
    }, [timeZone]);

    return (
        <div className="AiScoringStatus" data-testid="ai-scoring-status">
            <div className="question-status">
                {/* TODO: check tooltip messages content */}
                <span
                    className="ai-scoring-icon"
                    data-testid="ai-scoring-status-icon"
                >
                    <GuxIcon
                        iconName="restricted/custom/sparkles-solid"
                        decorative={true}
                    />
                    {/* TODO: once updating to Spark v4, move GuxTooltip to the place right below the main div element and apply `hideTooltips()` function to the last processed date element */}
                    {!showProcessingStatus && (
                        <GuxTooltip>
                            {t.status.tooltipText({
                                answeredQuestionCount:
                                    totalQuestionsWithAiAnswer,
                                totalQuestionCount: totalQuestions,
                            })}
                        </GuxTooltip>
                    )}
                </span>
                <div>
                    <div className="content">
                        <span
                            className="ai-scoring-label"
                            data-testid="ai-scoring-status-label"
                        >
                            {t.label}
                        </span>
                        <PreviewTag
                            disabled={false}
                            showTooltip={showProcessingStatus}
                        />
                        <div className="vertical-line"></div>
                        {/*In order for the text inside span to be announced
                        by screen readers, the span itself that contains
                        role="status" is required to always be present.
                        If the text inside span is empty, it will not be announced*/}
                        <div className="ai-scoring-message">
                            <span
                                data-testid="ai-scoring-status-message"
                                role="status"
                                aria-atomic="true"
                            >
                                {showProcessingStatus
                                    ? aiScoringDateExists || hasAiScoringFailed
                                        ? t.status.reprocessing
                                        : t.status.processing
                                    : t.status.questionCount({
                                          count: totalQuestionsWithAiAnswer,
                                      })}
                            </span>
                            {showProcessingStatus ? (
                                <GuxRadialLoading context="input" />
                            ) : (
                                <GuxTooltip>
                                    {t.status.tooltipText({
                                        answeredQuestionCount:
                                            totalQuestionsWithAiAnswer,
                                        totalQuestionCount: totalQuestions,
                                    })}
                                </GuxTooltip>
                            )}
                        </div>
                    </div>
                    {lastProcessedDate && !showProcessingStatus && (
                        <div className="last-processed-date">
                            <span data-testid="last-processed-date" dir="auto">
                                {t.status.lastProcessed({
                                    dateLastChanged:
                                        lastProcessedDate?.toLocaleString(
                                            locale,
                                            options
                                        ),
                                })}
                            </span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
